import { useState } from 'react';
import { Button } from '../ui/button';
import { sourceLabel } from '../../lib/leads/sourceLabels';
import { formatDuration, speakerNote } from '../../lib/speedToLead/callLogView';
import { playbackStartSeconds, segmentsFromOffset } from '../../lib/speedToLead/callMedia';
import { LEAD_OUTCOME_LABELS, type LeadOutcome } from '../../types/leadOutcome.js';
import { LeadOutcomePicker } from './LeadOutcomePicker';
import type { CallLogRow } from '../../types/leadCall';
import type { LeadHistoryPage } from '../../lib/speedToLead/leadHistory';
import { LeadHistoryPanel } from './LeadHistoryPanel';

// THE CALL LOG (Speed to Lead Stage 3C), shared by the Kyck settings page and the club's portal page: ONE component
// so both read the same way. The rows come projected from the server (callLogView.ts) with NO phone numbers, storage
// paths or URLs in them.
//
// ⚠️ PLAYBACK fetches a signed URL of at most five minutes on each press of Play and hands it to an <audio> element;
// it is never stored, logged or put in the page address. ⚠️ DELETE exists only when the host passes `onDelete` (the
// Kyck admin page): the club's portal never does. Nothing here fires on render: every request is a click.

export interface CallLogProps {
  calls: CallLogRow[];
  loadPlayUrl: (kind: CallLogRow['kind'], id: string) => Promise<string>;
  /** Kyck admin only. When omitted there is no delete control at all. */
  onDelete?: (kind: CallLogRow['kind'], id: string) => Promise<void>;
  /**
   * A PERSON confirms the LEAD's outcome (Kyck staff, or the club's own portal user). Omitted = read-only.
   */
  onConfirmOutcome?: (kind: CallLogRow['kind'], id: string, outcome: string) => Promise<void>;
  /** Stage 4C. Present = a "Lead history" button on each opened call. The panel loads only when opened. */
  loadHistory?: (kind: CallLogRow['kind'], id: string, cursor: string | null) => Promise<LeadHistoryPage>;
  /** The all-clients log names each call's club. Omitted (one club's log, the portal) = no club label. */
  clubLabel?: (row: CallLogRow) => string | null;
}

const when = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
};

const RECORDING_TEXT: Record<string, string> = {
  none: 'Not recorded',
  pending: 'Recording not ready yet',
  ready: 'Saving the recording',
  stored: 'Recording saved',
  failed: 'The recording could not be saved',
  deleted: 'Recording deleted',
  discarded_unaccepted: 'Not kept: nobody took the call',
};
const TRANSCRIPT_TEXT: Record<string, string> = {
  none: 'No transcript',
  pending: 'Transcript on its way',
  ready: 'Transcript',
  unavailable: 'Transcript unavailable',
  deleted: 'Transcript deleted',
};

function Row({ row, loadPlayUrl, onDelete, onConfirmOutcome, loadHistory, club }: { row: CallLogRow; club?: string | null } & Pick<CallLogProps, 'loadPlayUrl' | 'onDelete' | 'onConfirmOutcome' | 'loadHistory'>) {
  const [open, setOpen] = useState(false);
  const [src, setSrc] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Stage 4B: the confirmed outcome, once a person confirms it here (before the list refetches).
  const [confirmed, setConfirmed] = useState<{ outcome: string; label: string } | null>(null);
  const [changing, setChanging] = useState(false);
  const suggested = row.summary?.status === 'ready' ? row.summary.suggestedOutcome : null;
  const stored = row.outcomeRecord;
  const personConfirmed = confirmed !== null || stored?.source === 'person';
  const shownLabel = confirmed?.label ?? stored?.label ?? null;

  const recStatus = deleted ? 'deleted' : (row.recording?.status ?? 'none');
  const trStatus = deleted ? 'deleted' : (row.transcript?.status ?? 'none');
  // Stage 4D: the audio goes at its own (shorter) retention while the transcript stays 24 months.
  const recordingText = recStatus === 'deleted' && trStatus === 'ready' ? 'Recording deleted (retention)' : (RECORDING_TEXT[recStatus] ?? recStatus);

  const play = async () => {
    setBusy(true);
    setError(null);
    try {
      setSrc(await loadPlayUrl(row.kind, row.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the recording.');
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!onDelete) return;
    setBusy(true);
    setError(null);
    try {
      await onDelete(row.kind, row.id);
      setDeleted(true);
      setSrc(null);
      setConfirming(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="rounded border border-border bg-card text-sm" data-testid="call-row">
      <button type="button" className="flex w-full flex-wrap items-center justify-between gap-2 p-3 text-left" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span>
          <span className="font-medium text-foreground">{row.kind === 'inbound' ? 'Inbound call' : (row.leadName ?? 'Lead')}</span>
          {club && <span data-testid="call-club" className="ml-2 text-muted-foreground">· {club}</span>}
          {row.isTest && <span data-testid="test-badge" className="ml-2 rounded border border-border px-1.5 py-0.5 text-xs text-muted-foreground">Test</span>}
          <span className="ml-2 text-muted-foreground">{when(row.at)}</span>
        </span>
        <span className="flex items-center gap-3 text-muted-foreground">
          <span>{row.outcome}</span>
          {row.samFallback && <span data-testid="sam-fallback-chip">· {row.samFallback}</span>}
          {row.durationSeconds !== null && <span>{formatDuration(row.durationSeconds)}</span>}
          {recStatus === 'stored' && <span aria-label="Has a recording">●</span>}
        </span>
      </button>
      {open && (
        <div className="space-y-3 border-t border-border p-3" data-testid="call-detail">
          <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
            {row.source && <div><dt className="inline text-muted-foreground">Source: </dt><dd className="inline">{sourceLabel(row.source)}</dd></div>}
            {row.answeredBy && <div><dt className="inline text-muted-foreground">Answered by: </dt><dd className="inline">{row.answeredBy}</dd></div>}
            {row.samFallback && <div data-testid="sam-fallback-detail"><dt className="inline text-muted-foreground">Text follow-up: </dt><dd className="inline">{row.samFallback}</dd></div>}
            {row.speedToLeadSeconds !== null && <div><dt className="inline text-muted-foreground">Lead dialled after: </dt><dd className="inline">{formatDuration(row.speedToLeadSeconds)}</dd></div>}
            {row.endReason && <div><dt className="inline text-muted-foreground">Why it ended: </dt><dd className="inline">{row.endReason}</dd></div>}
          </dl>

          {(row.summary?.status === 'ready' || shownLabel || (onConfirmOutcome && row.summary)) && (
            <div className="space-y-2 rounded border border-border p-2" data-testid="call-summary">
              {row.summary?.status === 'ready' && row.summary.text && (
                <div className="space-y-1">
                  <p className="font-medium text-foreground">Summary <span className="text-xs font-normal text-muted-foreground">(written by AI from the transcript: check it)</span></p>
                  <p data-testid="call-summary-text">{row.summary.text}</p>
                  {row.summary.nextStep && <p className="text-muted-foreground">Next step: {row.summary.nextStep}</p>}
                </div>
              )}
              {row.summary && row.summary.status !== 'ready' && row.summary.status !== 'due' && row.summary.status !== 'running' && (
                <p className="text-muted-foreground">No summary was written for this call.</p>
              )}
              {(row.summary?.status === 'due' || row.summary?.status === 'running') && <p className="text-muted-foreground">Summary on its way.</p>}
              {shownLabel && (
                <p data-testid="call-outcome">
                  Outcome: <span className="font-medium text-foreground">{shownLabel}</span>{' '}
                  <span className="text-muted-foreground">{personConfirmed ? `(confirmed${confirmed ? '' : stored?.confirmedByName ? ` by ${stored.confirmedByName}` : ''})` : '(set automatically)'}</span>
                </p>
              )}
              {onConfirmOutcome && (!personConfirmed || changing) && (
                <div data-testid="call-outcome-picker" className="space-y-1">
                  <LeadOutcomePicker
                    id={`outcome-${row.kind}-${row.id}`}
                    current={changing ? ((confirmed?.outcome ?? stored?.outcome ?? 'unresolved') as LeadOutcome) : 'unresolved'}
                    suggested={changing ? null : (suggested as LeadOutcome | null)}
                    setBy={null}
                    onSave={async (code) => {
                      await onConfirmOutcome(row.kind, row.id, code);
                      setConfirmed({ outcome: code, label: LEAD_OUTCOME_LABELS[code] });
                      setChanging(false);
                    }}
                  />
                  {suggested && row.summary?.confidence && !changing && <p className="text-xs text-muted-foreground">{row.summary.confidence} confidence</p>}
                </div>
              )}
              {/* A host with no way to confirm (the lead split view: the LEAD's one outcome is set above the history) shows the suggestion as TEXT only. */}
              {!onConfirmOutcome && suggested && !personConfirmed && (
                <p className="text-muted-foreground" data-testid="call-suggestion-text">
                  Suggested by AI: {LEAD_OUTCOME_LABELS[suggested as LeadOutcome]}{row.summary?.confidence ? ` (${row.summary.confidence} confidence)` : ''}
                </p>
              )}
              {onConfirmOutcome && personConfirmed && !changing && <Button type="button" variant="ghost" size="sm" onClick={() => setChanging(true)}>Change</Button>}
            </div>
          )}

          {loadHistory && (
            <div className="space-y-2">
              <Button type="button" variant="outline" size="sm" aria-expanded={historyOpen} onClick={() => setHistoryOpen(!historyOpen)}>{historyOpen ? 'Hide lead history' : 'Lead history'}</Button>
              {historyOpen && <LeadHistoryPanel load={(cursor) => loadHistory(row.kind, row.id, cursor)} callHandlers={{ loadPlayUrl, onConfirmOutcome }} />}
            </div>
          )}

          {row.legs.length > 0 && (
            <ol className="space-y-1" aria-label="What happened, leg by leg">
              {row.legs.map((l, i) => (
                <li key={`${l.who}-${i}`} className="flex flex-wrap gap-x-3 text-muted-foreground">
                  <span className="text-foreground">{l.who}</span>
                  <span>{l.status}</span>
                  {l.startedAt && <span>{when(l.startedAt)}</span>}
                  {l.reason && <span>({l.reason})</span>}
                </li>
              ))}
            </ol>
          )}

          <div className="space-y-2">
            <p className="text-muted-foreground">{recordingText}{row.recording?.deleteAfter && recStatus === 'stored' ? ` (kept until ${when(row.recording.deleteAfter)})` : ''}</p>
            {recStatus === 'stored' && (
              <div className="flex flex-wrap items-center gap-3">
                <Button type="button" variant="outline" size="sm" onClick={() => void play()} disabled={busy}>{busy ? 'Loading…' : src ? 'Reload recording' : 'Play recording'}</Button>
                {src && (
                  <audio
                    controls
                    autoPlay
                    src={src}
                    className="h-9"
                    aria-label="Call recording"
                    // Skip the hold silence before the lead joined. The stored file is untouched.
                    onLoadedMetadata={(e) => {
                      const start = playbackStartSeconds(row.recordingOffsetSeconds);
                      if (start > 0) e.currentTarget.currentTime = start;
                    }}
                  />
                )}
                {onDelete && !confirming && <Button type="button" variant="ghost" size="sm" onClick={() => setConfirming(true)}>Delete recording and transcript</Button>}
                {onDelete && confirming && (
                  <span className="flex items-center gap-2">
                    <span className="text-destructive">This cannot be undone.</span>
                    <Button type="button" variant="destructive" size="sm" onClick={() => void remove()} disabled={busy}>Delete for good</Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setConfirming(false)}>Keep</Button>
                  </span>
                )}
              </div>
            )}
            {error && <p role="alert" className="text-destructive">{error}</p>}
          </div>

          {(trStatus !== 'none') && (
            <div className="space-y-1" data-testid="call-transcript">
              <p className="font-medium text-foreground">{TRANSCRIPT_TEXT[trStatus] ?? trStatus}</p>
              {trStatus === 'unavailable' && row.transcript?.reason && <p className="text-muted-foreground">{row.transcript.reason}</p>}
              {trStatus === 'ready' && !deleted && row.transcript?.text && (
                <div className="space-y-1 rounded bg-muted p-2">
                  {segmentsFromOffset(row.transcript.segments ?? [{ channel: 0, text: row.transcript.text, label: 'Unclear' }], row.recordingOffsetSeconds).map((s, i) => (
                    <p key={i}>
                      {row.transcript?.speakerLabels === 'labelled' && <span className="font-medium text-foreground">{s.label}: </span>}
                      {s.text}
                    </p>
                  ))}
                  {speakerNote(row.transcript) && <p className="text-xs text-muted-foreground" data-testid="speaker-note">{speakerNote(row.transcript)}</p>}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

export function CallLog({ calls, loadPlayUrl, onDelete, onConfirmOutcome, loadHistory, clubLabel }: CallLogProps) {
  if (calls.length === 0) return <p className="text-sm text-muted-foreground" data-testid="call-log-empty">No calls yet.</p>;
  return (
    <ul className="space-y-2" aria-label="Calls">
      {calls.map((c) => <Row key={`${c.kind}-${c.id}`} row={c} loadPlayUrl={loadPlayUrl} onDelete={onDelete} onConfirmOutcome={onConfirmOutcome} loadHistory={loadHistory} club={clubLabel ? clubLabel(c) : null} />)}
    </ul>
  );
}
